import { useEffect, useMemo, useState } from "react";
import { useMsal, useAccount, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import Helper from "utils/helper";
import { readMfaValidatedRaw } from "utils/mfaStorage";
import {
  recoverMsalSessionFromCache,
  resolveMsalAccount,
  buildAccountInfoFromCachedSession,
  applySyntheticAccountFromCache,
  isCachedIdTokenExpired,
} from "services/msalSessionRecovery";

/**
 * MSAL React's `useIsAuthenticated()` only reflects `accounts` in {@link MsalContext}.
 * After `handleRedirectPromise()` runs in `index.js` before paint, that context array
 * can still be empty on the first post-redirect renders while `instance.getAllAccounts()`
 * / `getActiveAccount()` are already populated — users briefly see public routes.
 *
 * `Helper.isLoggedIn()` reflects persisted `msal.idtoken` (e.g. “remember me” / trusted
 * device). Without it, a valid session can exist before MSAL hooks hydrate, which
 * mounted `PublicRoutes` on private URLs and hit the `*` → `/404` catch-all.
 *
 * After Edge cold start, MSAL account cache can be empty while app token/MFA keys
 * remain — {@link resolveMsalAccount} rebuilds from cached JWT when needed.
 *
 * @returns {{ account: import("@azure/msal-browser").AccountInfo | null, instance: import("@azure/msal-browser").IPublicClientApplication, inProgress: import("@azure/msal-browser").InteractionStatus, msalStartupPending: boolean, isAuthenticated: boolean, msalAccountRecovering: boolean }}
 */
export function useMsalAppSession() {
  const { instance, inProgress } = useMsal();
  const accountFromHook = useAccount();
  const isAuthenticatedFromContext = useIsAuthenticated();
  const [recoveryTick, setRecoveryTick] = useState(0);

  const msalStartupPending = inProgress === InteractionStatus.Startup;
  const hasPersistedSession =
    Helper.isLoggedIn() || readMfaValidatedRaw();
  const canFastRenderFromCache = Boolean(buildAccountInfoFromCachedSession());

  const account = useMemo(() => {
    void recoveryTick;
    return resolveMsalAccount(instance, accountFromHook);
  }, [instance, accountFromHook, recoveryTick]);

  const isAuthenticated = useMemo(() => {
    if (msalStartupPending && !hasPersistedSession) {
      return false;
    }
    return (
      isAuthenticatedFromContext ||
      !!account ||
      instance.getAllAccounts().length > 0 ||
      Helper.isLoggedIn() ||
      readMfaValidatedRaw()
    );
  }, [
    msalStartupPending,
    hasPersistedSession,
    isAuthenticatedFromContext,
    account,
    instance,
  ]);

  /** Only true when there is no fast cached-session path and MSAL is still starting. */
  const msalAccountRecovering =
    msalStartupPending &&
    hasPersistedSession &&
    !canFastRenderFromCache;

  useEffect(() => {
    if (accountFromHook || instance.getAllAccounts().length > 0) {
      return;
    }
    if (!hasPersistedSession) {
      return;
    }

    if (canFastRenderFromCache) {
      applySyntheticAccountFromCache(instance);
      setRecoveryTick((t) => t + 1);
    }

    let cancelled = false;
    recoverMsalSessionFromCache(instance).finally(() => {
      if (!cancelled) {
        setRecoveryTick((t) => t + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    accountFromHook,
    instance,
    hasPersistedSession,
    canFastRenderFromCache,
  ]);

  return {
    account,
    instance,
    inProgress,
    msalStartupPending,
    isAuthenticated,
    msalAccountRecovering,
  };
}

/**
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @returns {boolean}
 */
export function shouldForceInteractiveReLogin(instance) {
  const hasPersistedSession =
    Helper.isLoggedIn() || readMfaValidatedRaw();
  if (!hasPersistedSession) {
    return false;
  }
  const token = localStorage.getItem("msal.idtoken");
  if (token && !isCachedIdTokenExpired(token)) {
    return false;
  }
  return !resolveMsalAccount(instance, null);
}
